import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileTab } from './ProfileTab';
import { SecurityTab } from './SecurityTab';
import { PreferencesTab } from './PreferencesTab';
import { SubscriptionTab } from './SubscriptionTab';
import { NotificationSettingsTab } from './NotificationSettingsTab';
import { PracticeProfileTab } from './PracticeProfileTab';
import { useAuth } from '@/hooks/useAuth';

interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SettingsModal({ open, onOpenChange }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState('profile');
  const { profile } = useAuth();
  
  // Notifications and Practice Profile tabs are for funcionarios — everyone EXCEPT oposiciones plans
  const showFuncionarioTabs = !profile?.plan || !profile.plan.startsWith('oposiciones');
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Configuración de cuenta</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full bg-muted/50 p-1 rounded-xl h-auto gap-1 ${showFuncionarioTabs ? 'grid-cols-3 sm:grid-cols-6 sm:rounded-full sm:h-10' : 'grid-cols-4 sm:rounded-full sm:h-10'}`}>
            <TabsTrigger 
              value="profile" 
              className="rounded-lg sm:rounded-full data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm text-xs py-1.5"
            >
              Perfil
            </TabsTrigger>
            {showFuncionarioTabs && (
              <TabsTrigger 
                value="practice"
                className="rounded-lg sm:rounded-full data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm text-xs py-1.5"
              >
                Práctica
              </TabsTrigger>
            )}
            {showFuncionarioTabs && (
              <TabsTrigger 
                value="notifications"
                className="rounded-lg sm:rounded-full data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm text-xs py-1.5"
              >
                Avisos
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="preferences"
              className="rounded-lg sm:rounded-full data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm text-xs py-1.5"
            >
              Preferencias
            </TabsTrigger>
            <TabsTrigger 
              value="security"
              className="rounded-lg sm:rounded-full data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm text-xs py-1.5"
            >
              Seguridad
            </TabsTrigger>
            <TabsTrigger 
              value="subscription"
              className="rounded-lg sm:rounded-full data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm text-xs py-1.5"
            >
              Plan
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-6">
            <ProfileTab />
          </TabsContent>

          {showFuncionarioTabs && (
            <TabsContent value="practice" className="mt-6">
              <PracticeProfileTab />
            </TabsContent>
          )}

          {showFuncionarioTabs && (
            <TabsContent value="notifications" className="mt-6">
              <NotificationSettingsTab />
            </TabsContent>
          )}

          <TabsContent value="preferences" className="mt-6">
            <PreferencesTab />
          </TabsContent>

          <TabsContent value="security" className="mt-6">
            <SecurityTab />
          </TabsContent>

          <TabsContent value="subscription" className="mt-6">
            <SubscriptionTab />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

